/* Penoblo Gravur (CORE-04): Modal mit Textfeld, Schriftauswahl und Live-Vorschau.
   Schreibt Line-Item-Properties (Gravurtext, Schriftart) in das Produktformular
   über das form-Attribut der Hidden-Inputs. Property-Namen bewusst identisch zum
   Alt-Shop, damit Bestellabwicklung/Workflows weiter funktionieren. */
class PnbEngraving extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.openBtn = this.querySelector('[data-open]');
    this.input = this.querySelector('[data-text-input]');
    this.fontSelect = this.querySelector('[data-font-select]');
    this.preview = this.querySelector('[data-preview-text]');
    this.counter = this.querySelector('[data-counter]');
    this.propText = this.querySelector('[data-prop-text]');
    this.propFont = this.querySelector('[data-prop-font]');
    this.propHand = this.querySelector('[data-prop-hand]');
    this.handSelect = this.querySelector('[data-hand-select]');
    this.statusEl = this.querySelector('[data-status]');

    // Ohne Pflicht-Refs kann das Modul nicht arbeiten -> sauber aussteigen,
    // statt im connectedCallback zu werfen (was sonst das ganze Modul killt).
    if (!this.dialog || !this.openBtn || !this.input || !this.preview || !this.propText) return;

    // Listener an ein AbortController-Signal binden -> disconnectedCallback raeumt
    // sie bei Section-Re-Render/Move ab (sonst doppelte Listener + Doppel-Apply).
    const signal = (this._ac = new AbortController()).signal;

    this.openBtn.addEventListener('click', () => { if (!this.dialog.open) this.dialog.showModal(); }, { signal });
    this.querySelector('[data-close]')?.addEventListener('click', () => this.dialog.close(), { signal });
    this.querySelector('[data-apply]')?.addEventListener('click', () => this.apply(), { signal });
    // Zwei Entfernen-Buttons (Statuszeile + Dialog) — beide verdrahten
    this.querySelectorAll('[data-remove]').forEach((btn) => btn.addEventListener('click', () => this.clear(), { signal }));
    this.input.addEventListener('input', () => this.refreshPreview(), { signal });
    if (this.fontSelect) this.fontSelect.addEventListener('change', () => this.refreshPreview(), { signal });
    if (this.handSelect) this.handSelect.addEventListener('change', () => this.refreshPreview(), { signal });
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.dialog.close();
    }, { signal });
    this.initFeeGuard(signal);
    this.refreshPreview();
  }

  /* Sofortkauf (Shop Pay, PayPal — „Direkt zur Kasse“) laeuft an der
     Formular-Absendung vorbei, an der die Gebuehren-Logik haengt: der Stift
     ginge mit Gravur, aber ohne die 20 EUR in den Checkout (Befund
     22.09.2026). Solange eine kostenpflichtige Gravur gesetzt ist, wird der
     Sofortkauf deshalb ausgeblendet; „In den Warenkorb“ bleibt, und dort
     greift der Guard. Wird die Gravur entfernt, kommt der Knopf zurueck. */
  sofortkaufSchalten(sichtbar) {
    if (this.dataset.feeRequired !== 'true') return;
    const formId = this.propText.getAttribute('form');
    const form = formId ? document.getElementById(formId) : null;
    if (!form) return;
    form.querySelectorAll('.shopify-payment-button').forEach((btn) => {
      btn.style.display = sichtbar ? '' : 'none';
    });
  }

  /* Kostenpflichtige Gravur unter der Gratis-Schwelle (wie im alten Shop):
     Beim Absenden des Produktformulars MIT Gravur wird zuerst das
     Gravur-Produkt (data-fee-variant, im Shop 20 EUR) in den Warenkorb
     gelegt und erst danach der normale Kauf fortgesetzt — so rendert der
     Warenkorb-Drawer beide Positionen zusammen. Der Eingriff haengt als
     Capture-Listener am document, damit er VOR Dawns product-form greift.
     Schlaegt das Gebuehren-Hinzufuegen fehl, wird der Kauf trotzdem
     fortgesetzt (kein Verkaufs-Blocker). */
  initFeeGuard(signal) {
    if (this.dataset.feeRequired !== 'true') return;
    const feeVariant = parseInt(this.dataset.feeVariant || '', 10);
    if (!feeVariant) return;
    const formId = this.propText.getAttribute('form');
    const form = formId ? document.getElementById(formId) : null;
    if (!form) return;
    this._feePassthrough = false;
    document.addEventListener('submit', (e) => {
      if (e.target !== form) return;
      if (this._feePassthrough) { this._feePassthrough = false; return; }
      if (this.propText.disabled || !this.propText.value) return; // ohne Gravur normal kaufen
      e.preventDefault();
      e.stopPropagation();
      const qtyInput = form.querySelector('[name="quantity"]');
      const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      const submitter = e.submitter || form.querySelector('[type="submit"]');
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: feeVariant, quantity: qty }] }),
      })
        .catch(() => {})
        .finally(() => {
          this._feePassthrough = true;
          if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter || undefined);
          else form.submit();
        });
    }, { capture: true, signal });
  }

  disconnectedCallback() {
    if (this._ac) this._ac.abort();
  }

  currentFont() {
    if (!this.fontSelect) return null;
    return this.fontSelect.options[this.fontSelect.selectedIndex];
  }

  /* Schriftname fuer die Bestellung: data-label (Attribute bleiben bei
     Browser-Uebersetzung unangetastet), sonst der Wert der Option — nie der
     sichtbare Text. */
  static fontLabel(option) {
    if (!option) return '';
    return option.getAttribute('data-label') || option.value || '';
  }

  refreshPreview() {
    const value = this.input.value;
    this.preview.textContent = value || this.preview.dataset.placeholder;
    const font = this.currentFont();
    if (font) this.preview.style.fontFamily = "'pnb-engraving-" + font.value + "'";
    /* Linkshänder: Produktbild in der Vorschau spiegeln — wie im alten Shop
       (gravur-popup.js setzte .mirror auf dem Bild). */
    const media = this.querySelector('.pnb-engraving__preview-media img');
    if (media && this.handSelect) {
      media.classList.toggle('pnb-engraving__media--mirror', this.handSelect.value === 'Links');
    }
    if (this.counter) {
      // maxLength ist -1, wenn kein/leeres maxlength-Attribut gesetzt ist
      // (max_chars im Snippet leer) -> dann keinen negativen Zaehler zeigen.
      const max = this.input.maxLength;
      this.counter.textContent = max > 0 ? max - value.length : '';
    }
  }

  apply() {
    const value = this.input.value.trim();
    if (!value) {
      this.clear();
      return;
    }
    this.propText.value = value;
    this.propText.disabled = false;
    if (this.fontSelect && this.propFont) {
      /* Fester Schriftname aus data-label, nie der sichtbare Text: eine
         Browser-Uebersetzung (Chrome „Seite uebersetzen“) tauscht den Text
         der Optionen aus, und genau der landete in der Bestellung
         („Victoria“ in chinesischen Zeichen, Befund 17.09.2026). */
      this.propFont.value = PnbEngraving.fontLabel(this.fontSelect.options[this.fontSelect.selectedIndex]);
      this.propFont.disabled = false;
    }
    if (this.handSelect && this.propHand) {
      this.propHand.value = this.handSelect.value;
      this.propHand.disabled = false;
    }
    if (this.statusEl) {
      this.statusEl.hidden = false;
      const st = this.statusEl.querySelector('[data-status-text]');
      if (st) st.textContent = '„' + value + '"';
    }
    this.openBtn.textContent = this.openBtn.dataset.editLabel;
    this.sofortkaufSchalten(false);
    this.dialog.close();
  }

  clear() {
    this.input.value = '';
    this.propText.value = '';
    this.propText.disabled = true;
    if (this.propFont) {
      this.propFont.value = '';
      this.propFont.disabled = true;
    }
    if (this.propHand) {
      this.propHand.value = 'Rechts';
      this.propHand.disabled = true;
    }
    if (this.handSelect) this.handSelect.value = 'Rechts';
    if (this.statusEl) this.statusEl.hidden = true;
    this.openBtn.textContent = this.openBtn.dataset.addLabel;
    this.sofortkaufSchalten(true);
    this.refreshPreview();
    this.dialog.close();
  }
}

if (!customElements.get('pnb-engraving')) {
  customElements.define('pnb-engraving', PnbEngraving);
}
